import React, { useState, useEffect, useRef } from 'react';
import { Popover } from 'antd';
import { CaretDownOutlined, CheckOutlined } from '@ant-design/icons';
import NoData from '../NoData';
import './CustomLabelSelect.scss';

// 自定义选择组件
const CustomSelect = ({
  options = [],         // 选项列表，格式为 [{value, label}]
  mode = 'single',      // 模式：single 或 multiple 或 tree
  onChange = () => { }, // 变化时的回调函数
  className = '',       // 自定义类名
  labelName = 'label',  // 标签字段名称
  valueName = 'value',  // 值字段名称
  customLabel = '数据', // 自定义标签文本
  value = [],           // 受控值
}) => {
  // 状态管理
  const [selectedValues, setSelectedValues] = useState(value);
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState(null);
  const [currentHoverPath, setCurrentHoverPath] = useState(null);
  const [isPopupHovered, setIsPopupHovered] = useState(false);
  const [popupTimeout, setPopupTimeout] = useState(null);
  const [superData, setSuperData] = useState([]);

  const ref = useRef(null);
  const popupRef = useRef(null);

  // 监听值变化，更新选中状态
  useEffect(() => {
    if(!Array.isArray(value)) return 
    const isEqual = 
    value.length === selectedValues.length && 
    value.every((val, index) => val === selectedValues[index]);
  
  if (!isEqual) {
    setSelectedValues(value);
  }
  }, [value]);

  // 监听 selectedValues 变化，更新 superData
  useEffect(() => {
    if (mode === 'tree' && Array.isArray(selectedValues)) {
      const newSuperData = selectedValues.map((item) => item.substring(0, 2)) || [];
      // 比较新数组和旧数组是否完全相同，避免无效更新
      const isEqual = newSuperData.length === superData.length 
        && newSuperData.every((val, index) => val === superData[index]);
      if (!isEqual) {
        setSuperData(newSuperData);
      }
    }
  }, [selectedValues, mode, superData]); // 增加 superData 作为依赖，用于比较
  // 清理定时器
  useEffect(() => {
    return () => {
      if (popupTimeout) {
        clearTimeout(popupTimeout);
      }
    };
  }, [popupTimeout]);

  const handleMouseLeave = (e) => {
    const relatedTarget = e.relatedTarget;
    const currentElement = e.currentTarget;

    // 检查 relatedTarget 是否为有效节点
    if (!relatedTarget || !(relatedTarget instanceof Node)) {
      return;
    }

    // 检查 currentElement 是否为有效节点
    if (!(currentElement instanceof Node)) {
      return;
    }

    // 检查 popupRef 是否已挂载
    const popupElement = popupRef.current;
    const isPopupValid = popupElement instanceof Node;

    const isMovingToChild = relatedTarget && (
      currentElement.contains(relatedTarget) ||
      (isPopupValid && popupElement.contains(relatedTarget))
    );

    if (!isMovingToChild) {
      const timeout = setTimeout(() => {
        if (!isMouseInRelatedArea()) {
          setShowPopup(false);
          setPopupContent(null);
          setCurrentHoverPath(null);
        }
      }, 300);

      setPopupTimeout(timeout);
    }
  };

  const handleMouseEnter = (content, path) => {
    if (popupTimeout) {
      clearTimeout(popupTimeout);
      setPopupTimeout(null);
    }
    setShowPopup(true);
    setPopupContent(content);
    setCurrentHoverPath(path);
  };

  const handlePopupMouseEnter = () => {
    if (popupTimeout) {
      clearTimeout(popupTimeout);
    }
    setIsPopupHovered(true);
    setPopupTimeout(null);
  };

  const handlePopupMouseLeave = (e) => {
    const relatedTarget = e.relatedTarget;

    // 最基本的检查：确保有目标元素且是Node类型
    if (!relatedTarget || !(relatedTarget instanceof Node)) {
      return;
    }

    // 获取并验证popup元素
    const popupElement = popupRef.current;
    if (!popupElement || !(popupElement instanceof Node)) {
      return;
    }

    // 使用try-catch包裹contains调用，防止意外错误
    let isInPopup = false;
    try {
      isInPopup = popupElement.contains(relatedTarget);
    } catch (error) {
      isInPopup = false;
    }

    // 获取并验证相关项列表
    let isInRelatedItem = false;
    try {
      const relatedItems = Array.from(
        document.querySelectorAll('.job-li-item, .child-item-wrapper')
      ).filter(el => el instanceof Node && document.body.contains(el));

      isInRelatedItem = relatedItems.some(el => {
        try {
          return el.contains(relatedTarget);
        } catch (error) {
          return false;
        }
      });
    } catch (error) {
      isInRelatedItem = false;
    }

    const isMovingToRelatedItem = isInPopup || isInRelatedItem;

    if (!isMovingToRelatedItem) {
      const timeout = setTimeout(() => {
        setShowPopup(false);
        setPopupContent(null);
        setCurrentHoverPath(null);
        setIsPopupHovered(false);
      }, 300);

      setPopupTimeout(timeout);
    }
  };

  // 检查鼠标是否在相关区域
  const isMouseInRelatedArea = () => {
    const popupElement = popupRef.current;
    if (!popupElement) return false;

    const mouseX = window.event?.clientX;
    const mouseY = window.event?.clientY;

    if (!mouseX || !mouseY) return false;

    const rect = popupElement.getBoundingClientRect();
    return (
      mouseX >= rect.left && mouseX <= rect.right &&
      mouseY >= rect.top && mouseY <= rect.bottom
    );
  };

  const renderContent = () => {
    // 单选或多选模式内容
    return (
      <div className='custom-select-open'>
        {options && options.length > 0 ? (
          <ul className='select-node-box'>
            {options.map((item) => (
              <li
                key={item[valueName]}
                className={`select-node-item ${selectedValues.includes(item[valueName]) ? 'selected' : ''}`}
                onClick={() => handleOptionClick(item)}
              >
                {item[labelName]}
                {selectedValues.includes(item[valueName]) ? (
                  <div className="ui-icon-check">
                    <CheckOutlined />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <NoData width={80} height={80} />
        )}
      </div>
    );
  };

  const renderTreeContent = () => {
    // 树形选择模式内容
    return (
      <div className='custom-select-open'>
        {options && options.length > 0 ? (
          <ul className='select-node-box' onMouseLeave={handleMouseLeave}>
            {options.map((item) => (
              <li
                key={item[valueName]}
                title={item[labelName]}
                className={`treeSelect-node-item ${superData.includes(item[valueName]) ? 'selected' :''}`}
                onMouseEnter={() => handleMouseEnter(item, item.code)}>
                {item[labelName]}
                <img className='iboss' src={require("../../../img/pinzhunzhun/dataSource/iboss.svg")} alt="iboss"
                />
              </li>
            ))}
          </ul>
        ) : (
          <NoData width={80} height={80} />
        )}
        {showPopup && (
          <div
            className="Tree-custom-popup"
            ref={popupRef}
            onMouseEnter={handlePopupMouseEnter}
            onMouseLeave={handlePopupMouseLeave}
          >
            <p className='title'>{popupContent?.label}</p>
            <ul>
              {popupContent && popupContent.children.map((child) => (
                <li key={child.value}>
                  <h4>{child.label}</h4>
                  <div className='text'>
                    {child && (child?.children || []).map((i) => (
                      <a key={i.value} className={`job-li-item_a ${selectedValues.includes(i[valueName]) ? 'selected' : ''}`}
                        onClick={() => { handleOptionClick(i) }}
                        onMouseEnter={() => handleMouseEnter(popupContent, `${popupContent.value}/${i.value}`)}>
                        {i.label}
                      </a>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const handleOptionClick = (data) => {
    const newValue = selectedValues.includes(data[valueName])
      ? selectedValues.filter(v => v !== data[valueName]) // 移除已选中项
      : mode === 'multiple' || mode === 'tree'
        ? [...selectedValues, data[valueName]] // 多选模式添加新选中项
        : [data[valueName]]; // 单选模式替换选中项

    setSelectedValues(newValue);

    // 通知父组件值变化
    if (onChange) {
      onChange(newValue);
    }
  };

  const renderBox = () => {
    if (mode === 'tree') {
      return renderTreeContent();
    } else {
      return renderContent();
    }
  };

  return (
    <div ref={ref} className={`custom-select ${className}`}>
      <Popover
        content={renderBox()}
        trigger="click"
        placement="bottomLeft"
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      >
        <div className='custom-select-box'>
          {value && value.length > 0 ? (
            <div className='selected'>{customLabel + '(' + value.length + ')'} </div>
          ) : (
            customLabel
          )}
          <span className={value && value.length > 0 ? 'select-arrow selected' : 'select-arrow'}>
            <CaretDownOutlined />
          </span>
        </div>
      </Popover>
    </div>
  );
};

export default CustomSelect;